/**
 * Smooth, in-place switching between a brand page's series/division chips.
 *
 * pag-facet-chips.liquid renders plain `<a href>` tag-path links — that is
 * deliberate (a chip is a real URL, works with no JS, is crawlable, and is
 * exactly what a screen reader or a "open in new tab" expects). Left alone,
 * though, every click is a full document navigation: the browser tears the
 * page down and repaints it from scratch, which is the flash this file
 * exists to remove. Three things do that:
 *
 *   PREFETCH   The section fetch is real network time — a second or two on
 *              a slow connection — and no amount of client-side animation
 *              hides a wait that hasn't happened yet. So the fetch is moved
 *              earlier: warmed on hover/focus and again on pointerdown,
 *              into sectionRenderer's own cache, so the click that actually
 *              navigates is very often a cache hit by the time it fires.
 *
 *   OPTIMISTIC ACTIVE STATE   Which chip becomes active is known the instant
 *              the link is clicked — it doesn't need the server's answer.
 *              Flipping it immediately (see setOptimisticActive) means the
 *              row itself never has anything to wait for.
 *
 *   THE GRID VEIL   The one thing that DOES have to wait is the product
 *              grid. Rather than hiding it (the old flash-to-blank), the
 *              current cards stay exactly where they are, softened and
 *              shimmering — see the CSS in pag-facet-chips.liquid — while
 *              the section re-renders underneath, invisibly, exactly as
 *              before. When it lands, the veil lifts with a GSAP stagger:
 *              the one moment in this file worth an authored reveal rather
 *              than a CSS transition, and this project's own "pag-" layer
 *              (pag-reveal, pag-card-hover) already standardises on GSAP for
 *              exactly that kind of bespoke motion.
 *
 * A network failure, or a browser with no fetch/URL support worth trusting,
 * falls back to a real navigation rather than a dead click.
 *
 * Two more things this file owns, both purely presentational (no navigation
 * involved):
 *
 *   THE SLIDING HOVER HIGHLIGHT   pag-facet-chips.liquid renders one shared
 *              `.pag-facet-chips__hover-highlight` per row. Rather than each
 *              chip fading its own background in and out, this file slides
 *              that one element (via chip.offsetLeft/offsetWidth) to sit
 *              behind whichever chip has the pointer or keyboard focus, so
 *              moving from one chip to the next reads as the same control
 *              following you rather than one indicator disappearing and
 *              another appearing in its place.
 *
 *   THE SCROLL FADE   The chip row scrolls horizontally when it overflows,
 *              with its native scrollbar hidden — nothing else says so. This
 *              file toggles a fade mask on whichever edge still has chips
 *              past it, the same "more here" cue a native scroller's own
 *              shadow gives, and clears it once the row is fully in view.
 *
 *   RE-FIRING `shopify:section:load`   A morphed section is server HTML
 *              re-applied to the existing DOM (see morph.js), which wipes
 *              any state a section's OWN init script had applied on top of
 *              that server markup — e.g. snippets/pag-facets-script.liquid
 *              un-hides its filter-drawer notch on boot, and a morph quietly
 *              restores the server's default `hidden` attribute, since
 *              neither sectionRenderer nor morph ever dispatch the event
 *              that script (and half a dozen other pag-*.js modules) listen
 *              on to redo that work. Re-dispatching it per morphed section
 *              after every navigate() is what keeps them working — this is
 *              a gap in the shared AJAX-render pipeline, not something
 *              specific to chip navigation, so it would just as easily bite
 *              the checkbox facets on any other AJAX-filtered collection.
 */

import { sectionRenderer } from '@theme/section-renderer';
import { whenGsapReady } from '@theme/pag-motion';
import { debounce } from '@theme/utilities';

const CHIP_SELECTOR = '.pag-facet-chips__chip';
const CHIP_ACTIVE_CLASS = 'pag-facet-chips__chip--active';
const CHIP_HOVERING_CLASS = 'pag-facet-chips__chip--hovering';
const ROW_SELECTOR = '.pag-facet-chips__row';
const HIGHLIGHT_SELECTOR = '.pag-facet-chips__hover-highlight';
const ROW_FADE_START_CLASS = 'pag-facet-chips__row--fade-start';
const ROW_FADE_END_CLASS = 'pag-facet-chips__row--fade-end';
const GRID_LOADING_CLASS = 'pag-grid-loading';

/**
 * Section keys, not full IDs — a page's real ID is `template--<n>__<key>`,
 * and only the suffix is known ahead of time. division_chips only exists on
 * SPL; querying for all four and keeping whichever are actually present is
 * simpler than teaching this file which brand it's on.
 */
const SECTION_KEYS = ['main', 'series_chips', 'division_chips', 'empty_state_enquiry'];

const PREFETCH_HOVER_DEBOUNCE_MS = 120;

const REVEAL_FROM = { opacity: 0.35, filter: 'blur(2px)' };
const REVEAL_TO = { opacity: 1, filter: 'blur(0px)', duration: 0.4, ease: 'power2.out', stagger: 0.035 };

const HIGHLIGHT_IN = { duration: 0.3, ease: 'power3.out' };
const HIGHLIGHT_OUT = { opacity: 0, duration: 0.18, ease: 'power1.out' };

/** Bumped on every navigate() call; a stale run bails instead of clobbering a newer one. */
let renderToken = 0;

/** Which `pathname+search` combinations have already been warmed, so hover/focus/pointerdown never repeat a fetch. */
const prefetchedKeys = new Set();

/**
 * The one chip currently under the sliding highlight, if any — a single
 * pointer/keyboard focus can only ever be on one chip at a time, so this
 * needs no per-row bookkeeping.
 * @type {{ row: Element, chip: Element, highlight: Element } | null}
 */
let hoverState = null;

/** @type {import('gsap').core.Tween | null} */
let highlightTween = null;

/**
 * @param {string} key - A section key from SECTION_KEYS.
 * @returns {Element | null} That section's `.shopify-section` wrapper, if present on this page.
 */
function findSectionWrapper(key) {
  const suffix = `__${key}`;
  for (const el of document.querySelectorAll('.shopify-section')) {
    if (el.id.endsWith(suffix)) return el;
  }
  return null;
}

/** @returns {string[]} The `shopify-section-…` IDs present on this page that this component keeps in sync. */
function collectSectionIds() {
  const ids = [];
  for (const key of SECTION_KEYS) {
    const wrapper = findSectionWrapper(key);
    if (wrapper) ids.push(wrapper.id.replace(/^shopify-section-/, ''));
  }
  return ids;
}

/**
 * Re-fires the lifecycle event a morph skips — see the RE-FIRING docblock
 * above. Dispatched on the section's own wrapper (bubbling), not `document`
 * directly, so pag-motion.js's `boot(event.target)` receives that wrapper as
 * its search root exactly as it would from a real section reload, rather
 * than re-scanning the whole document.
 * @param {string} id - A section ID, as returned by collectSectionIds().
 */
function dispatchSectionLoad(id) {
  const wrapper = document.getElementById(`shopify-section-${id}`);
  wrapper?.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
}

/**
 * @param {Element} target
 * @returns {URL | null} The chip's destination, or null for anything this
 *   component has no business intercepting (a different origin, a link
 *   that isn't actually a chip).
 */
function chipURL(target) {
  const link = target.closest(CHIP_SELECTOR);
  if (!(link instanceof HTMLAnchorElement)) return null;
  try {
    const url = new URL(link.href, window.location.href);
    return url.origin === window.location.origin ? url : null;
  } catch {
    return null;
  }
}

/**
 * Warms sectionRenderer's cache for every section this component tracks, at
 * the given URL. Safe to call repeatedly — prefetchedKeys short-circuits
 * anything already in flight or already cached.
 * @param {URL} url
 */
function prefetchSections(url) {
  const key = url.pathname + url.search;
  if (prefetchedKeys.has(key)) return;
  prefetchedKeys.add(key);

  for (const id of collectSectionIds()) {
    sectionRenderer.getSectionHTML(id, true, new URL(url)).catch(() => {
      prefetchedKeys.delete(key);
    });
  }
}

const debouncedPrefetch = debounce((/** @type {URL} */ url) => prefetchSections(url), PREFETCH_HOVER_DEBOUNCE_MS);

/**
 * Immediately marks `link` active within its own chip row and demotes
 * whichever sibling held that state — the destination is already known, so
 * the row has no reason to wait for the network to say so. Left alone if
 * the clicked chip was already active (that click is a "clear", not a
 * switch, and the server response settles it either way).
 * @param {HTMLAnchorElement} link
 */
function setOptimisticActive(link) {
  if (link.classList.contains(CHIP_ACTIVE_CLASS)) return;

  const row = link.closest('.pag-facet-chips__row');
  if (!row) return;

  row.querySelectorAll(`.${CHIP_ACTIVE_CLASS}`).forEach((chip) => {
    chip.classList.remove(CHIP_ACTIVE_CLASS);
    chip.removeAttribute('aria-current');
  });

  link.classList.add(CHIP_ACTIVE_CLASS);
  link.setAttribute('aria-current', 'page');
}

/**
 * Veils the product grid in place — see the CSS docblock in
 * pag-facet-chips.liquid for why this is a veil over the real cards rather
 * than skeleton content standing in for them.
 * @returns {Element | null} The grid element, to hand to revealGrid later.
 */
function applyGridVeil() {
  const grid = findSectionWrapper('main')?.querySelector('.product-grid') ?? null;
  grid?.classList.add(GRID_LOADING_CLASS);
  return grid;
}

/**
 * Lifts a grid veil applied by applyGridVeil, once the section behind it
 * has already been morphed into its final state.
 * @param {Element | null} grid
 */
async function revealGrid(grid) {
  if (!grid) return;

  const items = Array.from(grid.querySelectorAll(':scope > .product-grid__item'));
  grid.classList.remove(GRID_LOADING_CLASS);
  if (items.length === 0) return;

  const gsapReady = await whenGsapReady();
  if (!gsapReady || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  window.gsap.fromTo(items, REVEAL_FROM, REVEAL_TO);
}

/**
 * Slides the shared highlight (see pag-facet-chips.liquid) behind `chip`.
 * A no-op if it's already there — every pointer move within one chip's own
 * bounds re-fires pointerover on the same element.
 * @param {Element} chip
 */
async function moveHighlightTo(chip) {
  if (hoverState?.chip === chip) return;

  const row = chip.closest(ROW_SELECTOR);
  const highlight = row?.querySelector(HIGHLIGHT_SELECTOR);
  if (!row || !highlight) return;

  hoverState?.chip.classList.remove(CHIP_HOVERING_CLASS);
  chip.classList.add(CHIP_HOVERING_CLASS);
  hoverState = { row, chip, highlight };

  const x = /** @type {HTMLElement} */ (chip).offsetLeft;
  const y = /** @type {HTMLElement} */ (chip).offsetTop;
  const width = /** @type {HTMLElement} */ (chip).offsetWidth;
  const height = /** @type {HTMLElement} */ (chip).offsetHeight;

  const gsapReady = await whenGsapReady();

  // A newer hover target landed while this one was waiting on GSAP — let it win.
  if (hoverState?.chip !== chip) return;

  if (!gsapReady || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    Object.assign(highlight.style, { transform: `translate(${x}px, ${y}px)`, width: `${width}px`, height: `${height}px`, opacity: '1' });
    return;
  }

  highlightTween?.kill();
  highlightTween = window.gsap.to(highlight, { x, y, width, height, opacity: 1, ...HIGHLIGHT_IN });
}

/** Fades the shared highlight away — the pointer/focus left the row entirely, not just one chip for another. */
function hideHighlight() {
  if (!hoverState) return;
  const { chip, highlight } = hoverState;
  chip.classList.remove(CHIP_HOVERING_CLASS);
  hoverState = null;

  highlightTween?.kill();
  if (window.gsap) {
    highlightTween = window.gsap.to(highlight, HIGHLIGHT_OUT);
  } else {
    /** @type {HTMLElement} */ (highlight).style.opacity = '0';
  }
}

/**
 * @param {Element} chip - The chip the pointer/focus just left.
 * @param {EventTarget | null} relatedTarget - Where it went.
 */
function leftTheRow(chip, relatedTarget) {
  if (!(relatedTarget instanceof Element)) return true;
  return !chip.closest(ROW_SELECTOR)?.contains(relatedTarget);
}

/**
 * @param {Element} row
 */
function updateRowFade(row) {
  const maxScrollLeft = row.scrollWidth - row.clientWidth;
  const hasOverflow = maxScrollLeft > 2;
  const atStart = row.scrollLeft <= 2;
  const atEnd = row.scrollLeft >= maxScrollLeft - 2;

  row.classList.toggle(ROW_FADE_START_CLASS, hasOverflow && !atStart);
  row.classList.toggle(ROW_FADE_END_CLASS, hasOverflow && !atEnd);
}

function updateAllRowFades() {
  document.querySelectorAll(ROW_SELECTOR).forEach((row) => updateRowFade(row));
}

/**
 * @param {URL} url - The chip's destination.
 * @param {Object} [options]
 * @param {boolean} [options.pushState=true] - False when replaying a
 *   popstate, whose URL is already the address bar's.
 * @param {HTMLAnchorElement} [options.sourceLink] - The chip that was
 *   clicked, for the optimistic active-state update. Absent on popstate.
 */
async function navigate(url, { pushState = true, sourceLink } = {}) {
  const token = ++renderToken;
  const sectionIds = collectSectionIds();

  if (sectionIds.length === 0) {
    window.location.assign(url);
    return;
  }

  if (sourceLink) setOptimisticActive(sourceLink);
  const grid = applyGridVeil();

  try {
    // A fresh URL per call: buildSectionRenderingURL mutates its `url` argument's
    // searchParams in place, so N calls sharing one URL object would each stomp
    // the last one's `section_id` param.
    await Promise.all(sectionIds.map((id) => sectionRenderer.renderSection(id, { url: new URL(url) })));

    // A newer click landed while this one was in flight — let it own the page.
    if (token !== renderToken) return;

    if (pushState) history.pushState({}, '', url);

    // See the RE-FIRING docblock above — every morphed section's own init
    // script (the filter drawer among them) needs this to redo whatever it
    // layered on top of the server markup a morph just reapplied verbatim.
    sectionIds.forEach(dispatchSectionLoad);

    // The chip count/order a row offers can change with the tag path (a
    // division swap can add or drop an entire series row), so the fade
    // state is re-checked rather than assumed to still be correct.
    updateAllRowFades();

    await revealGrid(grid);
  } catch (error) {
    window.location.assign(url);
  }
}

/**
 * @param {MouseEvent} event
 * @returns {boolean} True for a plain left click — anything a browser would
 *   otherwise open in the same tab.
 */
function isPlainLeftClick(event) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/** Hover/focus entering a chip: warms its prefetch and slides the highlight onto it. @param {Event} event */
function onChipEnter(event) {
  if (!(event.target instanceof Element)) return;
  const chip = event.target.closest(CHIP_SELECTOR);
  if (!chip) return;

  const url = chipURL(event.target);
  if (url) debouncedPrefetch(url);

  moveHighlightTo(chip);
}

/** Hover/focus leaving a chip: hides the highlight only once it has left the row entirely. @param {Event} event */
function onChipLeave(event) {
  if (!(event.target instanceof Element)) return;
  const chip = event.target.closest(CHIP_SELECTOR);
  if (!chip || hoverState?.chip !== chip) return;

  if (leftTheRow(chip, /** @type {FocusEvent | PointerEvent} */ (event).relatedTarget)) hideHighlight();
}

/** @param {PointerEvent} event */
function onPointerDown(event) {
  if (!(event.target instanceof Element)) return;
  const url = chipURL(event.target);
  if (!url) return;
  debouncedPrefetch.cancel();
  prefetchSections(url);
}

/** @param {MouseEvent} event */
function onDocumentClick(event) {
  if (event.defaultPrevented) return;
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest(CHIP_SELECTOR);
  if (!(link instanceof HTMLAnchorElement)) return;
  if (!isPlainLeftClick(event)) return;
  if (link.target === '_blank') return;

  const url = chipURL(event.target);
  if (!url) return;

  event.preventDefault();

  if (url.pathname === window.location.pathname && url.search === window.location.search) return;

  navigate(url, { sourceLink: link });
}

document.addEventListener('click', onDocumentClick);

// Hover and keyboard focus both signal intent early enough to warm the
// cache (and both slide the highlight, so a keyboard user gets the same cue
// a mouse does); pointerdown catches everything else (touch, a fast click
// with no preceding hover) right at the last possible moment before the click.
document.addEventListener('pointerover', onChipEnter);
document.addEventListener('pointerout', onChipLeave);
document.addEventListener('focusin', onChipEnter);
document.addEventListener('focusout', onChipLeave);
document.addEventListener('pointerdown', onPointerDown);

// `scroll` doesn't bubble, but it still reaches a capture-phase listener on
// an ancestor — same trick as the pointer delegation above — so one
// listener here covers every current and future (post-morph) chip row.
document.addEventListener(
  'scroll',
  (event) => {
    if (event.target instanceof Element && event.target.matches(ROW_SELECTOR)) updateRowFade(event.target);
  },
  true
);
window.addEventListener('resize', debounce(updateAllRowFades, 150));
updateAllRowFades();

window.addEventListener('popstate', () => {
  navigate(new URL(window.location.href), { pushState: false });
});
